import './styles.css';
import React, { useState } from 'react';
import { Spin, Tag, Typography, Image, Button, Tooltip } from 'antd';
import { LoadingOutlined, WarningOutlined, UploadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { FileOutlined } from '@ant-design/icons';
// import QuoteStore from '../stores/QuoteStore';
import * as api from '@partsbadger/utils';

import { UploadFile } from '@partsbadger/library';

import { observer } from 'mobx-react';
import { QuoteLineItemProps } from '@partsbadger/types';
import ThreeDViewerModal from '../ThreeDViewerModal';

import { AnalysisAlert } from '../AnalysisAlert';

const { Text } = Typography;

interface IProduct {
    file_2d?: number;
    file_3d?: number;
}
interface IProps {
    item: QuoteLineItemProps;
    isStaffUser: boolean;
    reanalyzeMasterProduct: () => void;
    quoteToken: string;
    onRemove: (value: 'file_2d' | 'file_3d') => void;
    isRequote: boolean;
    onUpload: (value: IProduct) => void;
    isRapidQuote: boolean;
}

export const DisplayImage: React.FC<IProps> = observer(
    ({ item, isStaffUser, reanalyzeMasterProduct, quoteToken, isRequote, onUpload, onRemove, isRapidQuote }) => {
        const product = item.product;
        const { files_2d } = product?.attachments || {};
        const analysis2dStatus = item.product.analysis_status?.file_2d;
        const has2dError = analysis2dStatus?.status === 'FAILURE';
        const errorMsg = analysis2dStatus?.message;
        const readOnly = !item.master_product_details.editable;

        const [url, setUrl] = useState<string | null>(null);
        const [modelLoading, setModelLoading] = useState(false);
        const [modalVisible, setModalVisible] = useState(false);

        const upload3dUrl = isRapidQuote ? `customer/quote/${quoteToken}/3d-file/` : undefined;
        /** Same JSON URL as Parts Library / quote detail — stable, no signed-URL roundtrip (customer-portal PartsManager). */
        const direct3dModelUrl = product?.attachments?.file3d?.file_3djs ?? null;

        const open3dViewer = async () => {
            setModalVisible(true);
            if (url) return;
            if (direct3dModelUrl) {
                setUrl(direct3dModelUrl);
                return;
            }
            setModelLoading(true);
            try {
                const data = await api.get(`/customer/quote/${quoteToken}/threed-signed-url/${item.uid}`);
                setUrl(data.url);
            } catch (error) {
                console.log(error);
            } finally {
                setModelLoading(false);
            }
        };

        const open2dViewer = async (url: string) => {
            window.open(url, '_blank', 'width=800,height=800,left=200,top=200');
        };

        const { analysis_status, has_file_3d, has_file_2d, thumbnail, attachments } = product;

        const analyzer_3d_finished = analysis_status.file_3d.completed;
        const analyzer_3d_success = analysis_status?.file_3d?.status === 'SUCCESS';
        const analyzer3dFailure = analysis_status?.file_3d?.status === 'FAILURE';

        const handleOnRemove = () => {
            console.log('File removed');
        };

        const handleOnChange = ({ file_2d, file_3d }: { file_2d?: number; file_3d?: number }) => {
            if (file_2d) {
                onUpload({ file_2d });
            }

            if (file_3d) {
                onUpload({ file_3d });
            }
        };

        const defaultFileList = files_2d?.id
            ? [
                  {
                      uid: files_2d.id.toString(),
                      name: files_2d.filename,
                      //   status: 'done',
                      response: {
                          id: files_2d.id,
                          file: files_2d.file,
                          filename: files_2d.filename,
                      },
                  },
              ]
            : [];

        const AlertIcon = has2dError ? (
            <Tooltip title={errorMsg}>
                <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginLeft: '8px' }} />
            </Tooltip>
        ) : null;

        return (
            <>
                <div className="flex flex-col items-center justify-center xl:items-start xl:justify-start">
                    {!analyzer_3d_finished ? (
                        <Spin
                            indicator={<LoadingOutlined />}
                            spinning={!analyzer_3d_finished}
                            tip={`Analyzing Geometry...`}
                        />
                    ) : (
                        <>
                            <div
                                style={{
                                    width: '80px',
                                    height: '80px',
                                }}
                                aria-label="Part thumbnail"
                                className="d-flex flex-column align-items-center justify-content-center text-center"
                            >
                                {thumbnail ? (
                                    <div className="rounded-lg overflow-hidden border-2 border-slate-300 bg-slate-100">
                                        {has_file_3d ? (
                                            <Image
                                                preview={false}
                                                className="thumbnail-3d-file"
                                                alt={attachments?.file3d?.filename}
                                                src={thumbnail}
                                                onClick={() => {
                                                    open3dViewer();
                                                }}
                                            />
                                        ) : has_file_2d ? (
                                            <>
                                                <Image
                                                    preview={false}
                                                    className="thumbnail-2d-file"
                                                    src={thumbnail}
                                                    onClick={() => {
                                                        open2dViewer(attachments.files_2d.file);
                                                    }}
                                                />
                                            </>
                                        ) : null}
                                    </div>
                                ) : (
                                    <>
                                        <div
                                            className="flex items-center justify-center bg-slate-100  rounded cursor-pointer"
                                            onClick={open3dViewer}
                                        >
                                            <FileOutlined className="text-2xl text-slate-400" />
                                        </div>

                                        {has_file_3d && analyzer_3d_success && (
                                            <div>
                                                <Spin
                                                    size="small"
                                                    style={{ color: 'black' }}
                                                    indicator={<LoadingOutlined />}
                                                    spinning={has_file_3d}
                                                    tip={`Generating Preview...`}
                                                />
                                            </div>
                                        )}

                                        {!has_file_3d && !has_file_2d && (
                                            <div className="flex flex-col items-center justify-center mx-auto mt-3 mb-2 px-3 sm:px-0 text-center">
                                                <WarningOutlined
                                                    className="mb-1 text-2xl sm:text-3xl"
                                                    style={{ color: '#ff4d4f' }}
                                                />
                                                <Text
                                                    type="danger"
                                                    strong
                                                    className="text-sm sm:text-base font-size-md"
                                                >
                                                    No files uploaded yet
                                                </Text>
                                            </div>
                                        )}
                                    </>
                                )}
                                {/*{analyzer_3d_finished && !analyzer_3d_success && product.has_file_3d && quoteToken ? (*/}
                                {/*    <AnalysisAlert*/}
                                {/*        quoteToken={quoteToken}*/}
                                {/*        fileType="3D"*/}
                                {/*        analysisStatus={analysis_status.file_3d}*/}
                                {/*        isStaffUser={isStaffUser}*/}
                                {/*        reanalyzeMasterProduct={reanalyzeMasterProduct}*/}
                                {/*        handleOnChange={fileList => {*/}
                                {/*            const response = fileList[0]?.response;*/}
                                {/*            handleOnChange({ file_3d: response?.id });*/}
                                {/*        }}*/}
                                {/*        handleOnRemove={handleOnRemove}*/}
                                {/*    />*/}
                                {/*) : null}*/}

                                {/*<div style={{ width: '150px' }}>*/}
                                {/*    {((analyzer3dFailure && has_file_3d) || (!has_file_3d && !isRequote)) && (*/}
                                {/*        <div className="mt-1">*/}
                                {/*            <UploadFile*/}
                                {/*                fileType="3D"*/}
                                {/*                renderAs="button"*/}
                                {/*                customUrl={upload3dUrl}*/}
                                {/*                onChange={fileList => {*/}
                                {/*                    const response = fileList[0]?.response;*/}
                                {/*                    handleOnChange({ file_3d: response?.id });*/}
                                {/*                }}*/}
                                {/*                // onRemove={handleOnRemove}*/}
                                {/*            >*/}
                                {/*                <Button*/}
                                {/*                    className="my-custom-button"*/}
                                {/*                    size="small"*/}
                                {/*                    type={'primary'}*/}
                                {/*                    icon={<UploadOutlined />}*/}
                                {/*                >*/}
                                {/*                    Upload {analyzer3dFailure ? 'Again' : '3D File'}*/}
                                {/*                </Button>*/}
                                {/*            </UploadFile>*/}
                                {/*        </div>*/}
                                {/*    )}*/}
                                {/*</div>*/}

                                {/*<div style={{ width: '150px' }} className={'mt-2'}>*/}
                                {/*    <UploadFile*/}
                                {/*        fileType="2D"*/}
                                {/*        renderAs="button"*/}
                                {/*        onUpload={file => {*/}
                                {/*            // console.log('file', file);*/}
                                {/*        }}*/}
                                {/*        onChange={fileList => {*/}
                                {/*            const response = fileList[0]?.response;*/}
                                {/*            if (response) {*/}
                                {/*                handleOnChange({ file_2d: response?.id });*/}
                                {/*            }*/}
                                {/*        }}*/}
                                {/*        onRemove={() => onRemove('file_2d')}*/}
                                {/*        value={defaultFileList}*/}
                                {/*    >*/}
                                {/*        {!has_file_2d && !readOnly ? (*/}
                                {/*            <Button*/}
                                {/*                className="my-custom-button"*/}
                                {/*                size="small"*/}
                                {/*                type={'default'}*/}
                                {/*                icon={<UploadOutlined />}*/}
                                {/*                style={{ width: '140px', padding: '10px' }}*/}
                                {/*            >*/}
                                {/*                Add Drawing File*/}
                                {/*            </Button>*/}
                                {/*        ) : (*/}
                                {/*            <div*/}
                                {/*                className={'flex flex-row items-center justify-center'}*/}
                                {/*                onClick={e => e.stopPropagation()}*/}
                                {/*            >*/}
                                {/*                <span className="font-weight-bold">2D Drawing File:</span>*/}
                                {/*                {AlertIcon}*/}
                                {/*            </div>*/}
                                {/*        )}*/}
                                {/*    </UploadFile>*/}
                                {/*</div>*/}

                                {/* {item.product.has_file_3d && (
                                        <div>
                                            <Tag
                                                color="error"
                                                className="p-1 m-0"
                                                style={{ width: '100%', textAlign: 'center' }}
                                                icon={<WarningOutlined style={{ fontSize: '16px' }} />}
                                            >
                                                <div className="">3D Preview Unavailable</div>
                                            </Tag>
                                        </div>
                                    )} */}

                                {isRequote && item.master_product_details.editable && (
                                    <div className="d-flex justify-content-around py-3 text-center">
                                        <UploadFile
                                            renderAs="button"
                                            onChange={fileList => {
                                                const response = fileList[0]?.response;
                                                handleOnChange({ file_3d: response?.id });
                                            }}
                                            onRemove={handleOnRemove}
                                        >
                                            <Button
                                                className="my-custom-button"
                                                size="small"
                                                type={'primary'}
                                                icon={<UploadOutlined />}
                                            >
                                                Upload another file
                                            </Button>
                                        </UploadFile>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <ThreeDViewerModal
                    url={url}
                    visible={modalVisible}
                    onClose={() => {
                        setModalVisible(false);
                        setUrl(null);
                    }}
                    loading={modelLoading}
                />
            </>
        );
    }
);
